import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useTenantSettings, useSaveSection } from "@/hooks/useTenantSettings";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/settings/tenant/sections";
import { RegistrationPreview } from "@/components/settings/tenant/RegistrationPreview";
import { TextField } from "@/components/kit/fields";
import { FileUpload } from "@/components/kit/FileUpload";
import { Button } from "@/components/ui/button";

export function OrganizationProfile() {
  const { data, isLoading } = useTenantSettings();
  const save = useSaveSection();
  const { refresh } = useAuth();
  const [org, setOrg] = useState(null);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    if (data?.sections?.organization) setOrg(JSON.parse(JSON.stringify(data.sections.organization)));
    if (data?.sections?.branding) setBrand(JSON.parse(JSON.stringify(data.sections.branding)));
  }, [data]);

  if (isLoading || !org || !brand) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const so = (k, v) => setOrg((f) => ({ ...f, [k]: v }));
  const sb = (k, v) => setBrand((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    await save.mutateAsync({ section: "organization", data: org });
    await save.mutateAsync({ section: "branding", data: brand });
    await refresh();
    toast.success("Organization profile saved");
  };

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2" data-testid="organization-profile">
      <div className="space-y-5">
        <Card title="Partner network">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField label="Partner network name" value={org.partner_network_name} onChange={(v) => so("partner_network_name", v)} testId="op-network-name" hint="Shown everywhere — header, login, registration, portal." />
            <TextField label="Company name" value={org.legal_name} onChange={(v) => so("legal_name", v)} testId="op-company-name" />
            <TextField label="Contact email" value={org.contact_email} onChange={(v) => so("contact_email", v)} testId="op-contact-email" />
            <TextField label="Support email" value={org.support_email} onChange={(v) => so("support_email", v)} testId="op-support-email" />
            <TextField label="Website" value={org.website} onChange={(v) => so("website", v)} testId="op-website" />
            <TextField label="Country" value={org.country} onChange={(v) => so("country", v)} testId="op-country" />
          </div>
        </Card>

        <Card title="Contacts">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField label="Telegram" value={org.telegram} onChange={(v) => so("telegram", v)} testId="op-telegram" />
            <TextField label="WhatsApp" value={org.whatsapp} onChange={(v) => so("whatsapp", v)} testId="op-whatsapp" />
            <TextField label="Phone" value={org.phone} onChange={(v) => so("phone", v)} testId="op-phone" />
          </div>
        </Card>

        <Card title="Branding">
          <p className="mb-4 text-xs text-muted-foreground">Upload your logo, favicon and one side image for the login/registration pages. Everything else uses the platform design.</p>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FileUpload label="Logo" value={brand.logo_url} onChange={(v) => sb("logo_url", v)} kind="logo" aspect="logo" testId="op-logo" />
            <FileUpload label="Favicon" value={brand.favicon_url} onChange={(v) => sb("favicon_url", v)} kind="favicon" aspect="favicon" testId="op-favicon" />
          </div>
          <div className="mt-5">
            <FileUpload label="Registration / Login image (side panel)" value={brand.side_image_url} onChange={(v) => sb("side_image_url", v)} kind="login_bg" testId="op-side-image" hint="Shown on the left of login & registration. If empty, an auto-generated panel is used." />
          </div>
        </Card>

        <Button onClick={submit} disabled={save.isPending} data-testid="op-save" className="gap-2 bg-white text-black hover:bg-white/90">
          <Save className="h-4 w-4" /> Save profile
        </Button>
      </div>

      <div className="xl:sticky xl:top-4 xl:self-start">
        <RegistrationPreview networkName={org.partner_network_name} logo={brand.logo_url} sideImage={brand.side_image_url}
          fields={(data?.sections?.registration?.fields || []).filter((f) => f.enabled)} />
      </div>
    </div>
  );
}
